import type { ComponentChildren, JSX, Ref } from 'preact';
import type { DeliveryCard } from '~/utils/adTracking';

export type ExternalAdFormat = 'sponsorship' | 'strip' | 'row' | 'card' | 'pill' | 'toast';

export interface ExternalAdCard extends DeliveryCard {
    id: number;
    external: true;
    format: ExternalAdFormat;
    title: string;
    tagline: string | null;
    cta: string;
    link: string;
    images: { icon: string };
}

export interface FormatProps {
    ad: ExternalAdCard;
    linkRef: Ref<HTMLAnchorElement>;
}

/** Every external ad must read as an ad, whatever its format. */
export function AdLabel(): JSX.Element {
    return (
        <span class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Annons</span>
    );
}

export function AdIcon({ src, size, round }: { src: string; size: number; round?: boolean }) {
    return (
        <img
            src={src}
            alt=""
            width={size}
            height={size}
            loading="lazy"
            class={`block shrink-0 object-cover shadow-[0_0_0_1px_rgb(15_23_42/0.06)] ${
                round ? 'rounded-full' : 'rounded-[23%]'
            }`}
            style={{ width: `${size}px`, height: `${size}px` }}
        />
    );
}

export function AdCta({ children }: { children: ComponentChildren }) {
    return (
        <span class="shrink-0 whitespace-nowrap rounded-full bg-brand-100 px-3.5 py-1.5 text-sm font-semibold text-brand-800 transition-colors group-hover:bg-brand-600 group-hover:text-white">
            {children}
        </span>
    );
}

function AdLink({
    ad,
    linkRef,
    class: className,
    children,
}: FormatProps & { class: string; children: ComponentChildren }) {
    return (
        <a
            ref={linkRef}
            href={ad.link}
            target="_blank"
            rel="sponsored noopener"
            aria-label={`Annons: ${ad.title}${ad.tagline ? ` – ${ad.tagline}` : ''}`}
            class={`group ${className}`}
        >
            {children}
        </a>
    );
}

/** One line under a page heading. */
export function Sponsorship(props: FormatProps) {
    const { ad } = props;
    return (
        <AdLink
            {...props}
            class="mx-auto -mt-1 mb-5 flex w-fit max-w-full items-center gap-2 rounded-full bg-brand-50 py-1 pl-1 pr-3 text-sm text-slate-600"
        >
            <AdIcon src={ad.images.icon} size={26} />
            <span class="min-w-0 truncate">
                Topplistan presenteras av{' '}
                <b class="font-semibold text-brand-900 decoration-brand-300 underline-offset-[3px] group-hover:underline">
                    {ad.title}
                </b>
            </span>
            <AdLabel />
        </AdLink>
    );
}

/** A slim row between list items. */
export function Strip(props: FormatProps) {
    const { ad } = props;
    return (
        <AdLink {...props} class="flex items-center gap-3 border-b border-gray py-3">
            <AdIcon src={ad.images.icon} size={36} />
            <span class="flex min-w-0 flex-1 flex-col leading-tight md:flex-row md:items-baseline md:gap-1.5">
                <span class="flex items-baseline gap-2 md:contents">
                    <b class="font-semibold text-slate-900">{ad.title}</b>
                    <span class="md:order-last md:ml-auto">
                        <AdLabel />
                    </span>
                </span>
                {ad.tagline && <span class="truncate text-slate-500">{ad.tagline}</span>}
            </span>
            <AdCta>{ad.cta}</AdCta>
        </AdLink>
    );
}

/** A full-height row that sits in a podcast list like a podcast would. */
export function Row(props: FormatProps) {
    const { ad } = props;
    return (
        <AdLink
            {...props}
            class="flex flex-1 flex-row items-center justify-center border-b border-gray py-4"
        >
            <div class="relative mr-4 flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded shadow-xs transition-transform duration-200 ease-in-out group-hover:scale-110 lg:h-32 lg:w-32">
                <div
                    aria-hidden="true"
                    class="absolute inset-0 scale-150 bg-cover bg-center opacity-80 blur-xl"
                    style={{ backgroundImage: `url("${ad.images.icon}")` }}
                />
                <img
                    src={ad.images.icon}
                    alt=""
                    loading="lazy"
                    class="relative h-[62%] w-[62%] rounded-[23%] object-cover shadow-[0_6px_16px_-4px_rgb(0_0_0/0.25)]"
                />
            </div>
            <div class="mt-4 flex-1 self-start lg:mt-6">
                <span class="mb-1 block text-xs font-semibold uppercase tracking-wide text-brand-400">
                    Annons
                </span>
                <h2 class="font-medium text-black lg:text-md">{ad.title}</h2>
                {ad.tagline && (
                    <div class="mt-2 text-xs text-gray-600 sm:text-sm lg:text-base">
                        {ad.tagline}
                    </div>
                )}
            </div>
            <span class="ml-3 hidden sm:block">
                <AdCta>{ad.cta}</AdCta>
            </span>
        </AdLink>
    );
}

/** A small bordered card. */
export function Card(props: FormatProps) {
    const { ad } = props;
    return (
        <AdLink
            {...props}
            class="mt-3 flex items-center gap-3 rounded-md border border-slate-200 p-3 transition-colors hover:border-brand-300"
        >
            <AdIcon src={ad.images.icon} size={48} />
            <span class="min-w-0 flex-1 leading-tight">
                <span class="flex items-baseline gap-2">
                    <b class="font-semibold text-slate-900">{ad.title}</b>
                    <AdLabel />
                </span>
                {ad.tagline && (
                    <span class="block truncate text-sm text-slate-500">{ad.tagline}</span>
                )}
            </span>
            <AdCta>{ad.cta}</AdCta>
        </AdLink>
    );
}

/** A pill that sits among category tags. */
export function Pill(props: FormatProps) {
    const { ad } = props;
    return (
        <AdLink
            {...props}
            class="inline-flex items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-4 text-sm font-medium text-slate-900 transition-all hover:scale-105 hover:border-brand-300"
        >
            <AdIcon src={ad.images.icon} size={28} round />
            {ad.title}
            <AdLabel />
        </AdLink>
    );
}
